// industry-configuration.component.ts
import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { IndustryType, UpsertIndustryTypeRequest } from '../../../core/models/industry-type.model';
import { IndustryTypeService } from '../../../core/services/api-services/industry-type.service';
import { AuthService } from '../../../core/services/client-services/auth.service';
import { BreadcrumbComponent, BreadcrumbItem } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../../../shared/components/breadcrumb/service/breadcrumb.service';
import { PageEvent, PaginatorComponent } from '../../../shared/components/paginator/paginator.component';

declare const bootstrap: any;

@Component({
  selector: 'app-industry-configuration',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    BreadcrumbComponent,
    PaginatorComponent,
  ],
  templateUrl: './industry-configuration.component.html',
  styleUrl: './industry-configuration.component.css',
})
export class IndustryConfigurationComponent implements OnInit, OnDestroy {

  breadcrumbItems: BreadcrumbItem[] = [];
  private destroy$ = new Subject<void>();

  @ViewChild('industryOffcanvasEl') offcanvasElementRef!: ElementRef;

  private offcanvasInstance: any = null;

  // ── Table state ───────────────────────────────────────────────
  industries: IndustryType[] = [];
  filteredIndustries: IndustryType[] = [];
  pagedIndustries: IndustryType[] = [];
  isLoading = false;

  // ── Pagination ────────────────────────────────────────────────
  currentPage = 1;
  pageSize = 5;
  totalIndustries = 0;

  // ── Search ────────────────────────────────────────────────────
  searchText = '';

  // ── Offcanvas ─────────────────────────────────────────────────
  mode: 'add' | 'edit' = 'add';
  industryForm!: FormGroup;
  isSaving = false;
  selectedItem: IndustryType | null = null;
  private originalDescription = '';

  constructor(
    private fb: FormBuilder,
    private industryTypeService: IndustryTypeService,
    private breadcrumbService: BreadcrumbService,
    private toastr: ToastrService,
    public authService: AuthService,
  ) { }

  ngOnInit(): void {
    // Subscribe to breadcrumb changes
    this.breadcrumbService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.breadcrumbItems = items;
      });

    // Set breadcrumbs for this page
    this.breadcrumbService.setBreadcrumbs([
      { label: 'Dashboard', url: '/dashboard', icon: 'bi bi-house-door' },
      { label: 'Configuration', icon: 'bi bi-gear' },
      { label: 'Industry Configuration', icon: 'bi bi-building-gear' }
    ]);

    this.buildForm();
    this.loadIndustries();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Form ──────────────────────────────────────────────────────

  private buildForm(): void {
    this.industryForm = this.fb.group({
      industryDescription: [
        null,
        [
          Validators.required,
          Validators.maxLength(150),
          Validators.pattern(/^[a-zA-Z& ]+$/),
        ],
      ],
    });
  }

  // ── Data loading ──────────────────────────────────────────────

  loadIndustries(): void {
    this.isLoading = true;
    this.industryTypeService
      .getIndustryTypesPaginated(this.currentPage, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          if (res.success) {
            this.industries = res.data;
            this.totalIndustries = res.totalCount;
          } else {
            this.industries = [];
            this.totalIndustries = 0;
          }
          this.applySearch();
        },
        error: () => {
          this.isLoading = false;
          this.industries = [];
          this.applySearch();
          this.toastr.error('Failed to load industry types.');
        },
      });
  }

  // ── Pagination ─────────────────────────────────────────────────

  onPageChange(event: PageEvent): void {
    this.currentPage = event.page;
    this.pageSize = event.pageSize;
    this.loadIndustries();
  }

  // ── Search (client-side within current page) ───────────────────

  performSearch(): void {
    this.applySearch();
  }

  private applySearch(): void {
    const term = this.searchText.trim().toLowerCase();
    if (!term) {
      this.filteredIndustries = this.industries;
    } else {
      this.filteredIndustries = this.industries.filter(i =>
        String(i.industryTypeId).includes(term) ||
        i.industryDescription.toLowerCase().includes(term)
      );
    }
    this.pagedIndustries = this.filteredIndustries;
  }

  // ── Offcanvas ──────────────────────────────────────────────────

  openAddForm(): void {
    this.mode = 'add';
    this.selectedItem = null;
    this.originalDescription = '';
    this.industryForm.reset();
    this.showOffcanvas();
  }

  openEditForm(item: IndustryType): void {
    this.mode = 'edit';
    this.selectedItem = item;
    this.originalDescription = item.industryDescription;
    this.industryForm.patchValue({
      industryDescription: item.industryDescription,
    });
    this.showOffcanvas();
  }

  private showOffcanvas(): void {
    if (!this.offcanvasInstance) {
      this.offcanvasInstance = new bootstrap.Offcanvas(
        this.offcanvasElementRef.nativeElement,
        { backdrop: 'static', keyboard: false }
      );
    }
    this.offcanvasInstance.show();
  }

  closeOffcanvas(): void {
    this.offcanvasInstance?.hide();
  }

  // ── Save ───────────────────────────────────────────────────────

  onSave(): void {
    if (this.industryForm.invalid) {
      this.industryForm.markAllAsTouched();
      return;
    }

    const description: string = this.industryForm.value.industryDescription.trim();

    if (this.mode === 'edit' && description === this.originalDescription) {
      this.toastr.info('No changes detected. Please modify the description before saving.');
      return;
    }

    const payload: UpsertIndustryTypeRequest = {
      action: this.mode === 'add' ? 'INSERT' : 'UPDATE',
      industryTypeId: this.mode === 'edit' ? this.selectedItem?.industryTypeId : null,
      industryDescription: description,
    };

    this.isSaving = true;

    this.industryTypeService.upsertIndustryType(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isSaving = false;
          if (res.success) {
            this.toastr.success(
              this.mode === 'add'
                ? 'Industry type added successfully.'
                : 'Industry type updated successfully.',
            );
            this.closeOffcanvas();
            this.loadIndustries();
          } else {
            this.toastr.error(res.message || 'Failed to save industry type.');
          }
        },
        error: () => {
          this.isSaving = false;
          this.toastr.error('An unexpected error occurred.');
        },
      });
  }
}